import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
  createdAt: number
}

export interface Modal {
  id: string
  type: 'auth' | 'business-form' | 'property-form' | 'roommate-form' | 'community-join' | 'event-rsvp' | 'confirmation' | 'image-gallery'
  props?: Record<string, any>
  onClose?: () => void
}

export interface DrawerState {
  isOpen: boolean
  type?: 'navigation' | 'filters' | 'search' | 'notifications' | 'profile'
  content?: any
}

interface UIState {
  // Theme and appearance
  isDarkMode: boolean
  language: 'en' | 'am' // English or Amharic
  
  // Navigation and layout
  sidebarOpen: boolean
  mobileMenuOpen: boolean
  drawer: DrawerState
  
  // Loading states
  globalLoading: boolean
  pageLoading: boolean
  
  // Notifications
  notifications: Notification[]
  
  // Modals
  modals: Modal[]
  
  // Search
  searchQuery: string
  searchFocused: boolean
  recentSearches: string[]
  
  // Map settings
  mapCenter: { lat: number; lng: number }
  mapZoom: number
  
  // Cultural preferences
  showEthiopianCalendar: boolean
  culturalEventsNotifications: boolean
  
  // User preferences
  compactView: boolean
  notificationsEnabled: boolean
  locationPermission: 'granted' | 'denied' | 'prompt'
  
  // Connection status
  isOnline: boolean
  
  // Performance
  imageQuality: 'high' | 'medium' | 'low'
  dataUsageMode: 'normal' | 'savings'
}

const initialState: UIState = {
  isDarkMode: false,
  language: 'en',
  sidebarOpen: false,
  mobileMenuOpen: false,
  drawer: {
    isOpen: false,
  },
  globalLoading: false,
  pageLoading: false,
  notifications: [],
  modals: [],
  searchQuery: '',
  searchFocused: false,
  recentSearches: [],
  mapCenter: { lat: 39.0458, lng: -76.6413 }, // Default to Baltimore (large Ethiopian diaspora)
  mapZoom: 10,
  showEthiopianCalendar: true,
  culturalEventsNotifications: true,
  compactView: false,
  notificationsEnabled: true,
  locationPermission: 'prompt',
  isOnline: true,
  imageQuality: 'high',
  dataUsageMode: 'normal',
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Theme and appearance
    toggleDarkMode: (state) => {
      state.isDarkMode = !state.isDarkMode
    },
    setDarkMode: (state, action: PayloadAction<boolean>) => {
      state.isDarkMode = action.payload
    },
    setLanguage: (state, action: PayloadAction<'en' | 'am'>) => {
      state.language = action.payload
    },
    
    // Navigation and layout
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen
    },
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload
    },
    openDrawer: (state, action: PayloadAction<{ type: DrawerState['type']; content?: any }>) => {
      state.drawer = {
        isOpen: true,
        type: action.payload.type,
        content: action.payload.content,
      }
    },
    closeDrawer: (state) => {
      state.drawer = {
        isOpen: false,
      }
    },
    
    // Loading states
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.globalLoading = action.payload
    },
    setPageLoading: (state, action: PayloadAction<boolean>) => {
      state.pageLoading = action.payload
    },
    
    // Notifications
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'createdAt'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
        createdAt: Date.now(),
      }
      state.notifications.unshift(notification)
      
      // Auto-remove after duration (default 5 seconds)
      if (notification.duration !== 0) {
        setTimeout(() => {
          // This would need to be handled by the component that manages notifications
        }, notification.duration || 5000)
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },
    
    // Modals
    openModal: (state, action: PayloadAction<Omit<Modal, 'id'>>) => {
      const modal: Modal = {
        ...action.payload,
        id: Date.now().toString(),
      }
      state.modals.push(modal)
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter(m => m.id !== action.payload)
    },
    closeTopModal: (state) => {
      state.modals.pop()
    },
    clearModals: (state) => {
      state.modals = []
    },
    
    // Search
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setSearchFocused: (state, action: PayloadAction<boolean>) => {
      state.searchFocused = action.payload
    },
    addRecentSearch: (state, action: PayloadAction<string>) => {
      const query = action.payload.trim()
      if (query && !state.recentSearches.includes(query)) {
        state.recentSearches.unshift(query)
        // Keep only the 10 most recent searches
        state.recentSearches = state.recentSearches.slice(0, 10)
      }
    },
    clearRecentSearches: (state) => {
      state.recentSearches = []
    },
    
    // Map settings
    setMapCenter: (state, action: PayloadAction<{ lat: number; lng: number }>) => {
      state.mapCenter = action.payload
    },
    setMapZoom: (state, action: PayloadAction<number>) => {
      state.mapZoom = action.payload
    },
    
    // Cultural preferences
    toggleEthiopianCalendar: (state) => {
      state.showEthiopianCalendar = !state.showEthiopianCalendar
    },
    setEthiopianCalendar: (state, action: PayloadAction<boolean>) => {
      state.showEthiopianCalendar = action.payload
    },
    toggleCulturalEventsNotifications: (state) => {
      state.culturalEventsNotifications = !state.culturalEventsNotifications
    },
    setCulturalEventsNotifications: (state, action: PayloadAction<boolean>) => {
      state.culturalEventsNotifications = action.payload
    },
    
    // User preferences
    toggleCompactView: (state) => {
      state.compactView = !state.compactView
    },
    setCompactView: (state, action: PayloadAction<boolean>) => {
      state.compactView = action.payload
    },
    setNotificationsEnabled: (state, action: PayloadAction<boolean>) => {
      state.notificationsEnabled = action.payload
    },
    setLocationPermission: (state, action: PayloadAction<'granted' | 'denied' | 'prompt'>) => {
      state.locationPermission = action.payload
    },
    
    // Connection status
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload
    },
    
    // Performance
    setImageQuality: (state, action: PayloadAction<'high' | 'medium' | 'low'>) => {
      state.imageQuality = action.payload
    },
    setDataUsageMode: (state, action: PayloadAction<'normal' | 'savings'>) => {
      state.dataUsageMode = action.payload
    },
    
    // Reset UI state
    resetUI: () => initialState,
  },
})

export const {
  // Theme and appearance
  toggleDarkMode,
  setDarkMode,
  setLanguage,
  
  // Navigation and layout
  toggleSidebar,
  setSidebarOpen,
  toggleMobileMenu,
  setMobileMenuOpen,
  openDrawer,
  closeDrawer,
  
  // Loading states
  setGlobalLoading,
  setPageLoading,
  
  // Notifications
  addNotification,
  removeNotification,
  clearNotifications,
  
  // Modals
  openModal,
  closeModal,
  closeTopModal,
  clearModals,
  
  // Search
  setSearchQuery,
  setSearchFocused,
  addRecentSearch,
  clearRecentSearches,
  
  // Map settings
  setMapCenter,
  setMapZoom,
  
  // Cultural preferences
  toggleEthiopianCalendar,
  setEthiopianCalendar,
  toggleCulturalEventsNotifications,
  setCulturalEventsNotifications,
  
  // User preferences
  toggleCompactView,
  setCompactView,
  setNotificationsEnabled,
  setLocationPermission,
  
  // Connection status
  setOnlineStatus,
  
  // Performance
  setImageQuality,
  setDataUsageMode,
  
  // Reset
  resetUI,
} = uiSlice.actions

export default uiSlice.reducer